import React from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import Section from 'app/GNNPage/components/Section'
import InfoCard from 'app/GNNPage/components/InfoCard'
import MitraCard from 'app/GNNPage/components/MitraCard'
import {
  STRATEGI_GNN_CARDS,
  PENGUATAN_KETERAMPILAN_CARDS,
  MITRA_CARDS,
  TESTIMONY_CARDS,
} from 'app/GNNPage/data/cards'
import TestimonyCard from 'app/GNNPage/components/TestimonyCard'
import Accordion from 'app/GNNPage/components/Accordion'
import FAQS from 'app/GNNPage/data/faqs'
import { showTentangGnnDialog } from 'app/GNNPage/components/AboutGNNDialog'

const Home = () => {
  const router = useRouter()
  return (
    <main>
      <Section className="py-20" imgBackground="/images/gnn/banner.jpg">
        <Text
          as="h1"
          variant="display-lg"
          className="mb-5 !text-[42px] text-center"
          color="inverse"
          style={{
            textShadow:
              '0px 1px 0px rgb(0 0 0 / 0.075), 0px 1px 1px rgb(0 0 0 / 0.075), 0px 2px 2px rgb(0 0 0 / 0.075)',
          }}
        >
          Mahir Numerasi, Majukan Negeri
        </Text>
        <Text
          color="inverse"
          className="mb-8 max-w-3xl mx-auto !leading-relaxed !text-[0.985rem] text-center"
          style={{
            textShadow:
              '0px 1px 0px rgb(0 0 0 / 0.075), 0px 1px 1px rgb(0 0 0 / 0.075), 0px 2px 2px rgb(0 0 0 / 0.075)',
          }}
        >
          Gerakan Numerasi Nasional mengajak semua kalangan untuk menjadikan
          numerasi sebagai bagian dari kehidupan sehari-hari. Gerakan ini adalah
          semangat bersama untuk membangun bangsa dengan menumbuhkan budaya
          berpikir kritis, logis, dan analitis di berbagai lapisan masyarakat.
        </Text>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            color="white"
            className="group !bg-transparent !text-white !outline-white hover:!bg-white hover:!text-blue-80"
            onClick={() => {
              showTentangGnnDialog()
            }}
          >
            <Icon
              fontSize="sm"
              className="mr-1 !text-white group-hover:!text-blue-80"
            >
              info_outlined
            </Icon>
            Tentang GNN
          </Button>
          <Button
            color="white"
            className="!bg-yellow-400 hover:!bg-yellow-500 !outline-yellow-400 hover:!outline-yellow-500"
            onClick={() => {
              window
                .open(
                  'https://sindara.gurudikdas.kemendikdasmen.go.id/gerai-numerasi',
                  '_blank'
                )
                ?.focus()
            }}
          >
            Gerai Numerasi
          </Button>
        </div>
      </Section>
      <Section title="Strategi Gerakan Numerasi Nasional">
        <Text
          color="subdued"
          className="mb-10 max-w-3xl mx-auto !leading-relaxed !text-[0.985rem] text-center"
        >
          Gerakan Numerasi Nasional terdiri dari Catur Matra (Empat Pusat)
          Pendidikan, yaitu: Sekolah, Keluarga, Masyarakat dan Media. Gerakan
          Numerasi Nasional bertujuan meningkatkan kemampuan numerasi masyarakat
          Indonesia sehingga mampu memahami, menggunakan, dan
          menginterpretasikan konsep serta keterampilan numerasi dalam kehidupan
          sehari-hari untuk pengambilan keputusan yang lebih baik dan
          peningkatan kualitas hidup.
        </Text>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {STRATEGI_GNN_CARDS.map((detail, idx) => (
            <InfoCard
              key={idx}
              className="text-left"
              button={
                <Button
                  size="sm"
                  fullWidth
                  onClick={() => {
                    window.open(detail.button.url, '_blank')?.focus()
                  }}
                >
                  {detail.button.text}
                </Button>
              }
              content={
                <Text variant="body-sm" color="subdued" className="mb-5">
                  {detail.text}
                </Text>
              }
              icon={detail.icon}
              title={detail.title}
            />
          ))}
        </div>
      </Section>
      <Section title="Penguatan Keterampilan Numerasi">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {PENGUATAN_KETERAMPILAN_CARDS.map((detail, idx) => (
            <InfoCard
              key={idx}
              className="text-left"
              button={
                <Button
                  size="sm"
                  fullWidth
                  onClick={() => {
                    const url = detail.button.url
                    if (url.startsWith('https://')) {
                      window.open(detail.button.url, '_blank')?.focus()
                      return
                    }
                    router.push(detail.button.url)
                  }}
                >
                  {detail.button.text}
                </Button>
              }
              content={
                <Text variant="body-sm" color="subdued" className="mb-5">
                  {detail.text}
                </Text>
              }
              icon={detail.icon}
              title={detail.title}
            />
          ))}
        </div>
      </Section>
      <Section title="Program Mitra Pembangunan GNN">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-16">
          {MITRA_CARDS.map((props, idx) => (
            <MitraCard
              key={idx}
              icon={
                props.image ? (
                  <Image
                    alt="logo-mitra"
                    src={props.image}
                    height={54}
                    width={54}
                    layout="fixed"
                    objectFit="contain"
                  />
                ) : undefined
              }
              {...props}
            />
          ))}
        </div>
        <div className="flex flex-col justify-center space-y-2 text-center">
          <Text variant="body-bold">
            Ingin berbagi praktik baik tentang pelaksanaan GNN?
          </Text>
          <Text color="subdued">
            Unggah Bukti Karyamu di Ruang GTK dan mulai diskusi praktik baik
            bersama guru dan tenaga kependidikan lainnya.
          </Text>
          <Button
            color="white"
            className="!bg-yellow-400 hover:!bg-yellow-500 w-fit mx-auto !mt-6"
            onClick={() => {
              window.open('/bukti-karya', '_blank')?.focus()
            }}
          >
            Unggah Bukti Karya
          </Button>
        </div>
      </Section>
      <Section title="Testimoni">
        <Text variant="body-lg" color="subdued" className="mb-10 text-center">
          Apa kata mereka tentang Gerakan Numerasi Nasional
        </Text>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONY_CARDS.map((props, idx) => (
            <TestimonyCard
              key={idx}
              imgUrl={`/images/gnn/testimoni/${idx + 1}.jpg`}
              {...props}
            />
          ))}
        </div>
      </Section>
      <Section title="Pertanyaan yang Sering Diajukan">
        <Accordion
          data={FAQS.map(({ answer, question }) => ({
            label: question,
            value: answer,
          }))}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3"
        />
      </Section>
      <Section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Text
            as="h2"
            className="mb-5 text-center"
            color="inverse"
            variant="display-lg"
          >
            Siap Sukseskan Gerakan Numerasi Nasional?
          </Text>
          <Text className="mb-5 text-center" color="inverse">
            Mari ambil peran. Mulai dari{' '}
            <strong>sekolah, keluarga, masyarakat hingga ruang media</strong>.
            Bersama kita siap sukseskan Gerakan Numerasi Nasional!
          </Text>
          <Button
            color="white"
            className="!bg-yellow-400 hover:!bg-yellow-500 w-fit mx-auto !mt-8 !outline-yellow-400 hover:!outline-yellow-500"
            onClick={() => {
              window
                .open(
                  'https://sindara.gurudikdas.kemendikdasmen.go.id/gerai-numerasi',
                  '_blank'
                )
                ?.focus()
            }}
          >
            Gerai Numerasi
          </Button>
        </div>
      </Section>
    </main>
  )
}

export default Home
